import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOverview, getApplications, getScenarioComparison, getStream } from "@/lib/api";
import { useAppStore, SCENARIO_CAPTIONS } from "@/store/appStore";
import { statusMeta, fmtDate, STREAM_COLORS } from "@/lib/format";
import { StatusChip, StreamBadges } from "@/components/tmo/primitives";
import { RingGauge } from "@/components/tmo/GaugeRadial";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  FileText, Download, Printer, Eye, Briefcase, Wrench, Network, LayoutDashboard, TriangleAlert,
} from "lucide-react";

/* ---------------- helpers ---------------- */
function downloadCsv(filename, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
const AS_OF = "08/18/2026";

/* ---------------- catalog ---------------- */
const GROUPS = [
  {
    audience: "Executive Leadership", icon: Briefcase,
    reports: [
      { id: "exec-summary", title: "Executive Storm Readiness Summary", desc: "Overall readiness, stream breakdown, what's driving the score and this week's priorities.", source: "overview" },
      { id: "scenario-report", title: "Scenario Comparison Report", desc: "Readiness across Blue Sky → Beyond Black Sky, by stream.", source: "scenario" },
    ],
  },
  {
    audience: "Operations", icon: LayoutDashboard,
    reports: [
      { id: "app-register", title: "Application Readiness Register", desc: "The full estate: score, status, criticality and last test for every application.", source: "applications" },
      { id: "action-list", title: "Not-Ready & Overdue Action List", desc: "Applications that are not storm-ready or overdue for testing, ranked by severity.", source: "overview" },
      { id: "director-rollup", title: "Director Rollup Report", desc: "Readiness rolled up by Director — app counts, average score and ready/at-risk/not-ready mix.", source: "applications" },
      { id: "depth-report", title: "Load & Depth Test Report", desc: "AHOP and load-test depth per application, flagging apps with no depth evidence to capture.", source: "applications" },
    ],
  },
  {
    audience: "Stream Leads", icon: Wrench,
    reports: [
      { id: "stream-SUBSCRIBER", title: "Subscriber Readiness Report", desc: "Customer-facing systems readiness, drivers and at-risk apps.", source: "stream:SUBSCRIBER" },
      { id: "stream-TECHNICIAN", title: "Technician Readiness Report", desc: "Field-crew systems readiness, drivers and at-risk apps.", source: "stream:TECHNICIAN" },
      { id: "stream-EXECUTIVE", title: "Executive Readiness Report", desc: "Leadership information systems readiness, drivers and at-risk apps.", source: "stream:EXECUTIVE" },
      { id: "stream-SHARED", title: "Shared / Infrastructure Report", desc: "Shared platform readiness, drivers and at-risk apps.", source: "stream:SHARED" },
    ],
  },
  {
    audience: "Architecture", icon: Network,
    reports: [
      { id: "dependency-report", title: "Dependency Blast Radius Report", desc: "Hub blast radius and the hard-dependency map for the storm-response spine.", source: "static" },
    ],
  },
];

const HUBS = [
  ["Corporate Network Infrastructure", 14, 25, 1], ["SAP ECC / CRM", 13, 16, 12], ["SCADA", 2, 10, 2],
  ["ADMS – OMS / NetCadOps", 8, 8, 7], ["DataPower (API Gateway)", 6, 8, 5],
  ["SAP Data Services Data Pipelines", 3, 3, 1], ["ECDC Internet Firewall", 3, 3, 1], ["EAI", 3, 3, 1],
];
const HARD_DEPS = [
  ["Outage Tracker", "ADMS • EAI • Outage Tracker failover • SAP"], ["Report an Outage", "DataPower • SAP • ADMS"],
  ["ADMS (NetCadOps / OMS)", "SCADA (ping source) • SAP • EAI"], ["Mobile Data Service Suite", "ADMS • EAI"],
  ["Iris", "ADMS • EAI"], ["PAS (AMS)", "ADMS"], ["EMSweb", "ADMS"], ["Customer Notifications", "ADMS • SAP (customer master)"],
];

/* ---------------- report body renderers ---------------- */
function ReportShell({ title, children }) {
  return (
    <div id="report-print" className="bg-white">
      <div className="flex items-center justify-between border-b-2 border-cnp-navy pb-3 mb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-cnp-blue">CenterPoint Energy · TMO Resiliency</div>
          <h3 className="text-xl font-extrabold text-cnp-navy">{title}</h3>
        </div>
        <div className="text-right text-xs text-cnp-textMuted">
          <div>Data as of</div><div className="font-semibold text-cnp-text">{AS_OF} 10:00 AM</div>
        </div>
      </div>
      {children}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-5">
    <div className="text-xs font-bold uppercase tracking-wide text-cnp-textMuted mb-2">{title}</div>
    {children}
  </div>
);

const Th = ({ children, className = "" }) => <th className={`text-left py-2 px-2 font-semibold text-cnp-textMuted border-b border-cnp-border ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-1.5 px-2 border-b border-cnp-border/60 ${className}`}>{children}</td>;

function ExecSummary({ d }) {
  return (
    <ReportShell title="Executive Storm Readiness Summary">
      <div className="flex items-center gap-6 mb-5">
        <RingGauge value={d.overall.score} status={d.overall.status} size={120} label="CONFIDENCE" />
        <div>
          <StatusChip status={d.overall.status} size="lg" />
          <p className="text-sm text-cnp-text mt-2 max-w-xl">{d.verdict}</p>
        </div>
      </div>
      <Section title="Readiness by Stream">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {d.stream_cards.map((s) => (
            <div key={s.stream} className="rounded-lg border border-cnp-border p-3">
              <div className="text-xs font-semibold" style={{ color: STREAM_COLORS[s.stream] }}>{s.label}</div>
              <div className="text-2xl font-bold tnum" style={{ color: statusMeta(s.status).color }}>{s.score}%</div>
              <div className="text-[11px] text-cnp-textMuted">{s.ready} of {s.total} ready</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="What's Driving Readiness">
        <ul className="space-y-1 text-sm">
          {d.driving.slice(0, 6).map((x, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: x.direction === "positive" ? "#00A651" : x.direction === "negative" ? "#D0021B" : "#F5A623" }} />
              {x.text}
            </li>
          ))}
        </ul>
      </Section>
      <Section title="Key Priorities">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {d.priorities.map((p, i) => (
            <div key={i} className="rounded-lg bg-cnp-bg p-3">
              <div className="text-2xl font-bold tnum text-cnp-navy">{p.count}</div>
              <div className="text-[11px] text-cnp-textMuted leading-tight mt-0.5">{p.label}</div>
            </div>
          ))}
        </div>
      </Section>
    </ReportShell>
  );
}

function AppRegister({ d }) {
  return (
    <ReportShell title="Application Readiness Register">
      <p className="text-sm text-cnp-textMuted mb-3">{d.total} applications in scope.</p>
      <table className="w-full text-xs">
        <thead><tr><Th>Application</Th><Th>Stream</Th><Th>Criticality</Th><Th className="text-center">Score</Th><Th>Last Test</Th><Th>Status</Th></tr></thead>
        <tbody>
          {d.rows.map((r) => (
            <tr key={r.application_id}>
              <Td className="font-medium text-cnp-text">{r.name}</Td>
              <Td><StreamBadges streams={r.streams || [r.stream]} /></Td>
              <Td>{r.criticality}</Td>
              <Td className="text-center tnum font-semibold">{r.score}%</Td>
              <Td className="tnum">{fmtDate(r.last_test)}</Td>
              <Td><StatusChip status={r.status} /></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportShell>
  );
}

function ActionList({ d }) {
  return (
    <ReportShell title="Not-Ready & Overdue Action List">
      <Section title="Priorities">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {d.priorities.map((p, i) => (
            <div key={i} className="rounded-lg bg-cnp-bg p-3">
              <div className="text-2xl font-bold tnum text-cnp-red">{p.count}</div>
              <div className="text-[11px] text-cnp-textMuted leading-tight mt-0.5">{p.label}</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Applications Requiring Attention">
        <table className="w-full text-xs">
          <thead><tr><Th>Application</Th><Th>Stream</Th><Th>Issue</Th><Th>Last Test</Th><Th className="text-center">Score</Th><Th>Status</Th></tr></thead>
          <tbody>
            {d.at_risk_table.map((r) => (
              <tr key={r.application_id}>
                <Td className="font-medium text-cnp-text">{r.name}</Td>
                <Td><StreamBadges streams={r.streams || [r.stream]} /></Td>
                <Td>{r.issue}</Td>
                <Td className="tnum">{fmtDate(r.last_test)}</Td>
                <Td className="text-center tnum font-semibold">{r.score}%</Td>
                <Td><StatusChip status={r.status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </ReportShell>
  );
}

function ScenarioReport({ d }) {
  return (
    <ReportShell title="Scenario Comparison Report">
      <table className="w-full text-sm">
        <thead>
          <tr><Th>Stream</Th>{d.scenarios.map((s) => <Th key={s.key} className="text-center">{s.label}</Th>)}</tr>
        </thead>
        <tbody>
          <tr className="bg-cnp-bg/60">
            <Td className="font-bold text-cnp-navy">Overall</Td>
            {d.scenarios.map((s) => <Td key={s.key} className="text-center tnum font-bold" style={{ color: statusMeta(s.overall.status).color }}>{s.overall.score}%</Td>)}
          </tr>
          {["SUBSCRIBER", "TECHNICIAN", "EXECUTIVE", "SHARED"].map((sk) => (
            <tr key={sk}>
              <Td className="font-medium">{d.scenarios[0].streams.find((x) => x.stream === sk)?.label}</Td>
              {d.scenarios.map((s) => {
                const st = s.streams.find((x) => x.stream === sk);
                return <Td key={s.key} className="text-center tnum font-semibold" style={{ color: statusMeta(st?.status).color }}>{st?.score}%</Td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-cnp-textMuted mt-3">Overall shift Blue Sky → Beyond Black Sky: <b>{d.overall_delta > 0 ? "+" : ""}{d.overall_delta} pts</b>. Target {d.target}%.</p>
    </ReportShell>
  );
}

function StreamReport({ d }) {
  return (
    <ReportShell title={`${d.label} Readiness Report`}>
      <div className="flex items-center gap-6 mb-4">
        <RingGauge value={d.score} status={d.status} size={110} label="READY" />
        <div className="text-sm text-cnp-text max-w-xl">
          <StatusChip status={d.status} size="lg" />
          <p className="mt-2">{d.verdict}</p>
          <p className="text-xs text-cnp-textMuted mt-1">{d.breakdown.ready} of {d.breakdown.total} applications ready.</p>
        </div>
      </div>
      <Section title="TRI Drivers">
        <div className="grid grid-cols-3 gap-3">
          {d.drivers.map((dr) => (
            <div key={dr.label} className="rounded-lg border border-cnp-border p-3">
              <div className="text-xs font-medium text-cnp-text">{dr.label} <span className="text-cnp-textMuted">· {dr.weight}%</span></div>
              <div className="text-xl font-bold tnum" style={{ color: dr.value >= 90 ? "#00A651" : dr.value >= 70 ? "#F5A623" : "#D0021B" }}>{dr.value}%</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Applications">
        <table className="w-full text-xs">
          <thead><tr><Th>Application</Th><Th className="text-center">Score</Th><Th>Last Test</Th><Th>Confidence</Th><Th>Status</Th></tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.application_id}>
                <Td className="font-medium text-cnp-text">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    {r.name}
                    <StreamBadges streams={r.streams || []} only={d.stream} />
                  </span>
                </Td>
                <Td className="text-center tnum font-semibold">{r.score}%</Td>
                <Td className="tnum">{fmtDate(r.last_test)}</Td>
                <Td>{r.confidence}</Td>
                <Td><StatusChip status={r.status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </ReportShell>
  );
}

function directorRollup(rows) {
  const map = {};
  rows.forEach((r) => {
    const dir = r.director || "Unassigned";
    (map[dir] = map[dir] || { director: dir, total: 0, sum: 0, ready: 0, at_risk: 0, not_ready: 0 });
    const m = map[dir];
    m.total += 1; m.sum += (r.score || 0);
    if (r.status === "READY") m.ready += 1;
    else if (r.status === "AT_RISK") m.at_risk += 1;
    else if (r.status === "NOT_READY") m.not_ready += 1;
  });
  return Object.values(map).map((m) => ({ ...m, avg: Math.round(m.sum / m.total) }))
    .sort((a, b) => a.avg - b.avg);
}

function DirectorRollup({ d }) {
  const rollup = directorRollup(d.rows);
  return (
    <ReportShell title="Director Rollup Report">
      <p className="text-sm text-cnp-textMuted mb-3">Readiness by Director across {d.total} applications.</p>
      <table className="w-full text-sm">
        <thead><tr><Th>Director</Th><Th className="text-center">Apps</Th><Th className="text-center">Avg Score</Th><Th className="text-center">Ready</Th><Th className="text-center">At Risk</Th><Th className="text-center">Not Ready</Th></tr></thead>
        <tbody>
          {rollup.map((m) => (
            <tr key={m.director}>
              <Td className="font-medium text-cnp-navy">{m.director}</Td>
              <Td className="text-center tnum">{m.total}</Td>
              <Td className="text-center tnum font-bold" style={{ color: m.avg >= 90 ? "#00A651" : m.avg >= 70 ? "#F5A623" : "#D0021B" }}>{m.avg}%</Td>
              <Td className="text-center tnum text-cnp-green">{m.ready}</Td>
              <Td className="text-center tnum text-cnp-amber">{m.at_risk}</Td>
              <Td className="text-center tnum text-cnp-red">{m.not_ready}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportShell>
  );
}

function DepthReport({ d }) {
  const rows = [...d.rows].sort((a, b) => (a.test_depth_score ?? 999) - (b.test_depth_score ?? 999));
  const missing = rows.filter((r) => r.test_depth_missing);
  return (
    <ReportShell title="Load & Depth Test Report">
      <div className="mb-4 rounded-lg border border-cnp-red/30 bg-cnp-red/5 px-4 py-3 flex items-center gap-3" data-testid="depth-callout">
        <TriangleAlert size={18} className="text-cnp-red shrink-0" />
        <span className="text-sm text-cnp-text">
          <b>{missing.length}</b> of {rows.length} applications have <b>no depth evidence</b> — owners must capture AHOP and load-test results.
        </span>
      </div>
      <table className="w-full text-xs">
        <thead><tr><Th>Application</Th><Th>Stream</Th><Th>Depth Evidence</Th><Th className="text-center">Depth Score</Th><Th>Flag</Th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.application_id} className={r.test_depth_missing ? "bg-cnp-red/5" : ""}>
              <Td className="font-medium text-cnp-text">{r.name}</Td>
              <Td className="text-cnp-textMuted">{r.stream}</Td>
              <Td>{r.test_depth_label}</Td>
              <Td className="text-center tnum font-semibold" style={{ color: (r.test_depth_score ?? 0) >= 70 ? "#00A651" : (r.test_depth_score ?? 0) >= 45 ? "#F5A623" : "#D0021B" }}>{r.test_depth_score ?? "—"}</Td>
              <Td>{r.test_depth_missing ? <span className="inline-flex items-center gap-1 text-cnp-red font-semibold"><TriangleAlert size={12} /> Capture AHOP / load evidence</span> : <span className="text-cnp-green">OK</span>}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </ReportShell>
  );
}

function DependencyReport() {
  return (
    <ReportShell title="Dependency Blast Radius Report">
      <Section title="Hub Systems — Blast Radius">
        <table className="w-full text-sm">
          <thead><tr><Th>Upstream Component</Th><Th className="text-center">Direct</Th><Th className="text-center">Transitive</Th><Th className="text-center">Hard Edges</Th></tr></thead>
          <tbody>
            {HUBS.map((h) => (
              <tr key={h[0]}>
                <Td className="font-medium text-cnp-navy">{h[0]}</Td>
                <Td className="text-center tnum">{h[1]}</Td>
                <Td className="text-center tnum">{h[2]}</Td>
                <Td className="text-center tnum font-bold" style={{ color: h[3] >= 5 ? "#D0021B" : "#1F2937" }}>{h[3]}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section title="Hard Dependencies">
        <table className="w-full text-sm">
          <thead><tr><Th>Application</Th><Th>Requires</Th></tr></thead>
          <tbody>
            {HARD_DEPS.map((r) => (
              <tr key={r[0]}><Td className="font-medium text-cnp-navy">{r[0]}</Td><Td className="text-cnp-blue">{r[1]}</Td></tr>
            ))}
          </tbody>
        </table>
      </Section>
    </ReportShell>
  );
}

/* ---------------- preview modal ---------------- */
function ReportPreview({ report, scenario, onClose }) {
  const src = report.source;
  const { data, isLoading } = useQuery({
    queryKey: ["report", report.id, scenario],
    enabled: src !== "static",
    queryFn: () => {
      if (src === "overview") return getOverview(scenario);
      if (src === "applications") return getApplications({ scenario, page_size: 200 });
      if (src === "scenario") return getScenarioComparison();
      if (src.startsWith("stream:")) return getStream(src.split(":")[1], scenario);
      return Promise.resolve(null);
    },
  });

  const ready = src === "static" || (!isLoading && data);

  const handleCsv = () => exportCsv(report, data);
  const handlePrint = () => window.print();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="report-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-4 pr-6">
            <span>{report.title}</span>
            <span className="flex gap-2 no-print">
              <button onClick={handleCsv} disabled={!ready} data-testid="report-export-csv"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-cnp-border text-cnp-text hover:bg-cnp-bg disabled:opacity-40">
                <Download size={14} /> CSV
              </button>
              <button onClick={handlePrint} disabled={!ready} data-testid="report-print"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-cnp-navy text-white hover:bg-cnp-navyLight disabled:opacity-40">
                <Printer size={14} /> Print / PDF
              </button>
            </span>
          </DialogTitle>
        </DialogHeader>
        {!ready ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <>
            {report.id === "exec-summary" && <ExecSummary d={data} />}
            {report.id === "app-register" && <AppRegister d={data} />}
            {report.id === "action-list" && <ActionList d={data} />}
            {report.id === "scenario-report" && <ScenarioReport d={data} />}
            {report.id === "director-rollup" && <DirectorRollup d={data} />}
            {report.id === "depth-report" && <DepthReport d={data} />}
            {report.source.startsWith("stream:") && <StreamReport d={data} />}
            {report.id === "dependency-report" && <DependencyReport />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function exportCsv(report, data) {
  const id = report.id;
  if (id === "exec-summary") {
    const rows = [["Stream", "Score", "Status", "Ready", "Total"]];
    data.stream_cards.forEach((s) => rows.push([s.label, s.score, statusMeta(s.status).label, s.ready, s.total]));
    return downloadCsv("executive_readiness_summary.csv", rows);
  }
  if (id === "app-register") {
    const rows = [["Application", "Stream", "Criticality", "Score", "Status", "Last Test"]];
    data.rows.forEach((r) => rows.push([r.name, r.stream, r.criticality, r.score, statusMeta(r.status).label, r.last_test || ""]));
    return downloadCsv("application_readiness_register.csv", rows);
  }
  if (id === "action-list") {
    const rows = [["Application", "Stream", "Issue", "Score", "Status", "Last Test"]];
    data.at_risk_table.forEach((r) => rows.push([r.name, r.stream, r.issue, r.score, statusMeta(r.status).label, r.last_test || ""]));
    return downloadCsv("action_list.csv", rows);
  }
  if (id === "scenario-report") {
    const rows = [["Stream", ...data.scenarios.map((s) => s.label)]];
    rows.push(["Overall", ...data.scenarios.map((s) => s.overall.score)]);
    ["SUBSCRIBER", "TECHNICIAN", "EXECUTIVE", "SHARED"].forEach((sk) => {
      rows.push([data.scenarios[0].streams.find((x) => x.stream === sk)?.label,
        ...data.scenarios.map((s) => s.streams.find((x) => x.stream === sk)?.score)]);
    });
    return downloadCsv("scenario_comparison.csv", rows);
  }
  if (report.source.startsWith("stream:")) {
    const rows = [["Application", "Score", "Status", "Confidence", "Last Test"]];
    data.rows.forEach((r) => rows.push([r.name, r.score, statusMeta(r.status).label, r.confidence, r.last_test || ""]));
    return downloadCsv(`${report.id.toLowerCase()}.csv`, rows);
  }
  if (id === "director-rollup") {
    const rows = [["Director", "Apps", "Avg Score", "Ready", "At Risk", "Not Ready"]];
    directorRollup(data.rows).forEach((m) => rows.push([m.director, m.total, m.avg, m.ready, m.at_risk, m.not_ready]));
    return downloadCsv("director_rollup.csv", rows);
  }
  if (id === "depth-report") {
    const rows = [["Application", "Stream", "Depth Evidence", "Depth Score", "Missing Depth Evidence"]];
    data.rows.forEach((r) => rows.push([r.name, r.stream, r.test_depth_label, r.test_depth_score ?? "", r.test_depth_missing ? "YES" : "No"]));
    return downloadCsv("load_and_depth_test.csv", rows);
  }
  if (id === "dependency-report") {
    const rows = [["Upstream Component", "Direct Dependants", "Transitive Reach", "Hard Edges"]];
    HUBS.forEach((h) => rows.push(h));
    rows.push([], ["Application", "Requires"]);
    HARD_DEPS.forEach((r) => rows.push(r));
    return downloadCsv("dependency_blast_radius.csv", rows);
  }
}

/* ---------------- page ---------------- */
export default function ReportsPage() {
  const scenario = useAppStore((s) => s.scenario);
  const [active, setActive] = useState(null);

  return (
    <div className="space-y-6 pb-16" data-testid="reports-page">
      <style>{`@media print { body * { visibility: hidden !important; } #report-print, #report-print * { visibility: visible !important; } #report-print { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; } .no-print { display: none !important; } }`}</style>

      <div className="rounded-xl border border-cnp-border bg-white p-5 flex items-center gap-3" data-testid="reports-intro">
        <span className="h-10 w-10 rounded-lg bg-cnp-blue/10 text-cnp-blue flex items-center justify-center"><FileText size={20} /></span>
        <div>
          <h2 className="text-lg font-extrabold text-cnp-navy">Readiness Reports</h2>
          <p className="text-sm text-cnp-textMuted">Preview, export (CSV) or print (PDF) branded reports built from live dashboard data — scenario: <b>{SCENARIO_CAPTIONS[scenario]}</b>.</p>
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.audience}>
          <div className="flex items-center gap-2 mb-3">
            <g.icon size={16} className="text-cnp-textMuted" />
            <h3 className="text-sm font-bold uppercase tracking-wide text-cnp-textMuted">{g.audience}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {g.reports.map((r) => (
              <div key={r.id} className="rounded-xl border border-cnp-border bg-white p-4 flex flex-col transition-all duration-300 hover:shadow-md hover:-translate-y-0.5" data-testid={`report-card-${r.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="h-9 w-9 rounded-lg bg-cnp-navy/5 text-cnp-navy flex items-center justify-center shrink-0"><FileText size={17} /></span>
                  <div>
                    <div className="font-bold text-cnp-navy text-sm leading-tight">{r.title}</div>
                    <div className="text-xs text-cnp-textMuted mt-1 leading-snug">{r.desc}</div>
                  </div>
                </div>
                <div className="mt-auto pt-2 flex items-center gap-2">
                  <button onClick={() => setActive(r)} data-testid={`report-preview-${r.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-cnp-blue text-white hover:bg-cnp-blue/90 transition-colors">
                    <Eye size={14} /> Preview
                  </button>
                  <span className="text-[10px] text-cnp-textMuted ml-auto flex items-center gap-2">
                    <span className="inline-flex items-center gap-1"><Download size={11} /> CSV</span>
                    <span className="inline-flex items-center gap-1"><Printer size={11} /> PDF</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {active && <ReportPreview report={active} scenario={scenario} onClose={() => setActive(null)} />}
    </div>
  );
}
